import React from 'react';
import PropType from 'prop-types';
import {
  makeStyles, Typography, Box, Button, Divider,
} from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import moment from 'moment';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import EditableContent from './EditableContent';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    padding: theme.spacing(1),
  },
  info: {
    display: 'flex',
    alignContent: 'center',
    flexDirection: 'column',
    flexGrow: 1,
  },
  profileImage: {
    height: 48,
    width: 48,
    alignSelf: 'flex-start',
  },
  infoText: {
    marginLeft: theme.spacing(2),
    flexGrow: 1,
  },
  actions: {
    display: 'flex',
    height: 24,
    fontSize: 12,
  },
  name: {
    textTransform: 'capitalize',
    alignSelf: 'flex-end',
    fontSize: '19px',
    fontWeight: 300,
  },
  infoHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  divider: {
    width: 1,
  },
  content: {
    fontSize: 13,
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}));

export default function Message({
  resource,
  refresh,
  setLoading,
  editDisabled,
}) {
  const [editing, setEditing] = React.useState(false);
  const [content, setContent] = React.useState(resource.attr('content'));

  const classes = useStyles();

  const handleSave = () => {
    setLoading(true);
    resource.attr('content', content)
      .submit()
      .then(() => refresh())
      .catch((e) => {
        displayErrorMessage(e, e.errors);
      });
    setEditing(false);
  };

  const createdAt = moment(resource.attr('createdAt'));
  const from = resource.attr('from').toUpperCase();
  const canEdit = !editDisabled && resource.attr('canEdit') === 'true';

  return (
    <Box>
      <Box className={classes.root}>
        <Avatar
          className={classes.profileImage}
          alt={from}
          src={resource.relationship('profileImage').link('self')?.attr('url')}
        >
          {from.charAt(0)}
        </Avatar>
        <Box className={classes.info}>
          <div className={classes.infoText}>
            <div className={classes.infoHeader}>
              <Typography variant="h6" color="primary" className={classes.name}>
                {resource.attr('from')}
              </Typography>
              {canEdit && (
                <Box className={classes.actions}>
                  {editing ? (
                    <>
                      <Button color="primary" size="small" onClick={handleSave}>
                        Save
                      </Button>
                      <Divider flexItem classes={{ root: classes.divider }} />
                      <Button onClick={() => setEditing(false)} color="primary" size="small">
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button test="message-edit-action" onClick={() => setEditing(true)} color="primary" size="small">
                        Edit
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </div>
            <Typography variant="body1" color="primary">
              {`${createdAt.format('l')} ${createdAt.format('LT')}`}
            </Typography>
            <Box>
              {editing ? (
                <EditableContent
                  value={content}
                  resource={resource}
                  onChange={val => setContent(val)}
                  onCancel={() => {
                    setEditing(false);
                    setContent(resource.attr('content'));
                  }}
                  onSave={handleSave}
                  className={classes.content}
                />
              ) : (
                <RichTextDisplay
                  className={classes.content}
                  content={content}
                />
              )
            }
            </Box>
          </div>
        </Box>
      </Box>
    </Box>
  );
}

Message.defaultProps = {
  editDisabled: false,
};

Message.propTypes = {
  resource: PropType.object.isRequired,
  refresh: PropType.func.isRequired,
  setLoading: PropType.func.isRequired,
  editDisabled: PropType.bool,
};
